import React, { useState, useEffect, useCallback } from 'react';
import { reportingAPI, batchesAPI, featureFlagsAPI } from '../services/api';
import { normalizeApiListResponse } from '../utils/apiListNormalize';
import { BATCH_SEARCHABLE_SELECT_PROPS, sortBatchesForDisplay } from '../utils/batchDisplayLabel';
import StaffPageShell from '../components/StaffPageShell';
import SearchableSelect from '../components/Form/SearchableSelect';

const STUDENT_ANALYTICS_FLAG = 'reporting.student_analytics';

function CutTable({ title, rows, labelKey }) {
  if (!rows?.length) {
    return (
      <div className="p-4 rounded-lg theme-card">
        <h3 className="font-semibold theme-text mb-2">{title}</h3>
        <p className="text-sm theme-text-secondary">No data</p>
      </div>
    );
  }
  return (
    <div className="p-4 rounded-lg theme-card overflow-x-auto">
      <h3 className="font-semibold theme-text mb-3">{title}</h3>
      <table className="w-full theme-table text-sm">
        <thead>
          <tr>
            <th className="text-left">Name</th>
            <th className="text-right">Count</th>
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 25).map((row) => (
            <tr key={`${title}-${row[labelKey]}`}>
              <td>{row[labelKey]}</td>
              <td className="text-right">{row.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 25 && (
        <p className="text-xs theme-text-secondary mt-2">Showing top 25 of {rows.length}</p>
      )}
    </div>
  );
}

const ReportingDashboardPage = () => {
  const [selectedBatch, setSelectedBatch] = useState('');
  const [batches, setBatches] = useState([]);
  const [batchStats, setBatchStats] = useState(null);
  const [placementStats, setPlacementStats] = useState(null);
  const [moduleStats, setModuleStats] = useState(null);
  const [readinessStats, setReadinessStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsYear, setAnalyticsYear] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBatches();
    fetchPlacementStats();
    featureFlagsAPI
      .status([STUDENT_ANALYTICS_FLAG])
      .then((res) => {
        setAnalyticsEnabled(Boolean(res.data?.data?.enabled?.[STUDENT_ANALYTICS_FLAG]));
      })
      .catch(() => setAnalyticsEnabled(false));
  }, []);

  useEffect(() => {
    if (selectedBatch) {
      fetchBatchStatistics();
    }
  }, [selectedBatch]);

  const fetchAnalytics = useCallback(async () => {
    if (!analyticsEnabled) {
      setAnalytics(null);
      return;
    }
    setAnalyticsLoading(true);
    try {
      const params = { activeOnly: activeOnly ? 'true' : 'false' };
      if (analyticsYear) params.year = analyticsYear;
      const response = await reportingAPI.getStudentAnalytics(params);
      setAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      if (err.response?.data?.code === 'FEATURE_DISABLED') {
        setAnalyticsEnabled(false);
        setAnalytics(null);
      } else {
        setError(err.response?.data?.message || 'Failed to fetch analytics');
      }
    } finally {
      setAnalyticsLoading(false);
    }
  }, [activeOnly, analyticsYear, analyticsEnabled]);

  useEffect(() => {
    if (analyticsEnabled) {
      fetchAnalytics();
    }
  }, [fetchAnalytics, analyticsEnabled]);

  const fetchBatches = async () => {
    try {
      const response = await batchesAPI.getAll();
      setBatches(normalizeApiListResponse(response.data));
    } catch (err) {
      console.error('Error fetching batches:', err);
      setBatches([]);
    }
  };

  const fetchBatchStatistics = async () => {
    setLoading(true);
    setError('');
    try {
      const [batch, modules, readiness] = await Promise.all([
        reportingAPI.getBatchStatistics(selectedBatch),
        reportingAPI.getModuleCompletion(selectedBatch),
        reportingAPI.getInterviewReadiness(selectedBatch),
      ]);
      setBatchStats(batch.data);
      setModuleStats(modules.data);
      setReadinessStats(readiness.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlacementStats = async () => {
    try {
      const response = await reportingAPI.getPlacementStatistics();
      setPlacementStats(response.data);
    } catch (err) {
      console.error('Error fetching placement stats:', err);
    }
  };

  const totals = analytics?.totals;
  const cuts = analytics?.cuts;

  return (
    <StaffPageShell title="Reporting Dashboard" wide>
      {analyticsEnabled === false && (
        <div className="mb-6 p-4 rounded-lg theme-card theme-text-secondary text-sm">
          Student analytics cuts are turned off. Enable{' '}
          <code>{STUDENT_ANALYTICS_FLAG}</code> under Feature flags to use them.
        </div>
      )}

      {analyticsEnabled && (
      <div className="mb-8">
        <div className="flex flex-wrap items-end gap-4 mb-4">
          <h2 className="text-xl font-semibold theme-text w-full">Student analytics</h2>
          <label className="flex items-center gap-2 theme-text text-sm">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            Active students only (filters enrollment cuts)
          </label>
          <div>
            <label className="block text-sm theme-text-secondary mb-1">Year filter</label>
            <input
              type="number"
              min="2000"
              max="2100"
              placeholder="All years"
              value={analyticsYear}
              onChange={(e) => setAnalyticsYear(e.target.value)}
              className="theme-input px-3 py-2 rounded w-32"
            />
          </div>
          <button
            type="button"
            onClick={fetchAnalytics}
            className="theme-btn-primary px-4 py-2 rounded"
          >
            Refresh analytics
          </button>
        </div>

        {analyticsLoading && (
          <p className="theme-text-secondary mb-4">Loading analytics…</p>
        )}

        {totals && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
            {[
              ['Students', totals.students],
              ['Active', totals.activeStudents],
              ['Enrollments', totals.enrollments],
              ['Placed', totals.placedEnrollments],
              ['Fee records', totals.withFeeRecord],
              ['Concerns', totals.concerns],
              ['Readiness', totals.withReadinessRecord],
              ['Staging docs', totals.stagingDocuments],
            ].map(([label, value]) => (
              <div key={label} className="p-3 rounded-lg theme-card text-center">
                <div className="text-2xl font-bold theme-text-primary">{value ?? 0}</div>
                <div className="text-xs theme-text-secondary">{label}</div>
              </div>
            ))}
          </div>
        )}

        {cuts && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-8">
            <CutTable title="Batch-wise" rows={cuts.batchWise} labelKey="batch" />
            <CutTable title="Course-wise" rows={cuts.courseWise} labelKey="course" />
            <CutTable title="Category-wise" rows={cuts.categoryWise} labelKey="category" />
            <CutTable
              title="Year-wise (enrollment date)"
              rows={cuts.yearWiseEnrollmentDate}
              labelKey="year"
            />
            <CutTable
              title="Year-wise (batch start)"
              rows={cuts.yearWiseBatchStart}
              labelKey="year"
            />
            <CutTable title="Status-wise" rows={cuts.statusWise} labelKey="status" />
            <CutTable
              title="Placement status"
              rows={cuts.placementStatusWise}
              labelKey="placementStatus"
            />
            <CutTable title="Companies" rows={cuts.companyWise} labelKey="company" />
            <CutTable title="Domain-wise" rows={cuts.domainWise} labelKey="domain" />
            <CutTable title="Country" rows={cuts.locationCountryWise} labelKey="country" />
            <CutTable title="State" rows={cuts.locationStateWise} labelKey="state" />
            <CutTable title="City" rows={cuts.locationCityWise} labelKey="city" />
            <CutTable title="Fee / payment" rows={cuts.feeWise} labelKey="feeStatus" />
            <CutTable title="Interview readiness" rows={cuts.readinessWise} labelKey="readiness" />
            <CutTable title="Concern types" rows={cuts.concernTypeWise} labelKey="concernType" />
            <CutTable
              title="Concern status"
              rows={cuts.concernStatusWise}
              labelKey="resolutionStatus"
            />
            <CutTable title="Staging pipeline" rows={cuts.stagingStatusWise} labelKey="status" />
          </div>
        )}
      </div>
      )}

      <div className="mb-6">
        <h2 className="text-xl font-semibold theme-text mb-3">Batch detail</h2>
        <label className="block theme-text mb-2">Select Batch</label>
        <div style={{ minWidth: '16rem', maxWidth: '24rem' }}>
          <SearchableSelect
            value={selectedBatch}
            onChange={(e) => setSelectedBatch(e.target.value)}
            placeholder="Select a batch"
            options={sortBatchesForDisplay(batches)}
            {...BATCH_SEARCHABLE_SELECT_PROPS}
          />
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 rounded-lg theme-error-bg theme-error-text">{error}</div>
      )}

      {placementStats && (
        <div className="mb-6 p-6 rounded-lg theme-card">
          <h2 className="text-2xl font-semibold mb-4 theme-text">Placement Statistics</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold theme-text-primary">
                {placementStats.summary?.totalPlacements || 0}
              </div>
              <div className="theme-text-secondary">Total Placements</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold theme-text-primary">
                ₹{placementStats.summary?.avgStartingPackage || 0}
              </div>
              <div className="theme-text-secondary">Avg Package</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold theme-text-primary">
                {placementStats.summary?.uniqueCompanies || 0}
              </div>
              <div className="theme-text-secondary">Companies</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold theme-text-primary">
                ₹{placementStats.summary?.maxPackage || 0}
              </div>
              <div className="theme-text-secondary">Max Package</div>
            </div>
          </div>
        </div>
      )}

      {selectedBatch && batchStats && (
        <div className="mb-6 p-6 rounded-lg theme-card">
          <h2 className="text-2xl font-semibold mb-4 theme-text">
            Batch: {batchStats.batch?.name}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold theme-text-primary">
                {batchStats.statistics?.totalStudents || 0}
              </div>
              <div className="theme-text-secondary">Total Students</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold theme-text-primary">
                {batchStats.statistics?.placementBreakdown?.placed || 0}
              </div>
              <div className="theme-text-secondary">Placed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold theme-text-primary">
                {batchStats.statistics?.avgAttendance || 0}%
              </div>
              <div className="theme-text-secondary">Avg Attendance</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold theme-text-primary">
                {batchStats.statistics?.interviewReady || 0}
              </div>
              <div className="theme-text-secondary">Interview Ready</div>
            </div>
          </div>
        </div>
      )}

      {selectedBatch && moduleStats && (
        <div className="mb-6 p-6 rounded-lg theme-card">
          <h2 className="text-2xl font-semibold mb-4 theme-text">Module Completion</h2>
          <div className="mb-4">
            <div className="text-lg theme-text">
              Overall Completion: {moduleStats.overallCompletion?.completionRate || '0%'}
            </div>
            <div className="text-sm theme-text-secondary">
              {moduleStats.overallCompletion?.allModulesCompleted || 0} /{' '}
              {moduleStats.overallCompletion?.totalStudents || 0} students completed all modules
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full theme-table">
              <thead>
                <tr>
                  <th>Module</th>
                  <th>Completed</th>
                  <th>Total</th>
                  <th>Completion Rate</th>
                </tr>
              </thead>
              <tbody>
                {moduleStats.moduleStatistics?.map((module) => (
                  <tr key={module.moduleId}>
                    <td>{module.moduleName}</td>
                    <td>{module.completed}</td>
                    <td>{module.totalEnrollments}</td>
                    <td>{module.completionRate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedBatch && readinessStats && (
        <div className="mb-6 p-6 rounded-lg theme-card">
          <h2 className="text-2xl font-semibold mb-4 theme-text">Interview Readiness</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <div className="text-center">
              <div className="text-2xl font-bold theme-text-primary">
                {readinessStats.statistics?.interviewReady || 0}
              </div>
              <div className="theme-text-secondary">Interview Ready</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold theme-text-primary">
                {readinessStats.statistics?.resumeReady || 0}
              </div>
              <div className="theme-text-secondary">Resume Ready</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold theme-text-primary">
                {readinessStats.statistics?.avgMockInterviews || 0}
              </div>
              <div className="theme-text-secondary">Avg Mock Interviews</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold theme-text-primary">
                {readinessStats.statistics?.avgCommunicationRating || 0}/5
              </div>
              <div className="theme-text-secondary">Avg Communication</div>
            </div>
          </div>
        </div>
      )}

      {loading && <div className="text-center py-8 theme-text">Loading statistics...</div>}
    </StaffPageShell>
  );
};

export default ReportingDashboardPage;
